import React from 'react';
import {
    MessageSquare, GitBranch, ShoppingCart, CheckCircle, XCircle,
    Play, Zap, Clock, Tag, GripVertical, Trash2, Copy,
    ChevronRight
} from 'lucide-react';

// Node type configurations
export const NODE_TYPES = {
    start: {
        label: 'Start',
        icon: Play,
        color: 'emerald',
        description: 'Entry point of the conversation',
        maxOutputs: 1,
        hasInput: false,
    },
    message: {
        label: 'Send Message',
        icon: MessageSquare,
        color: 'sky',
        description: 'AI sends a message to the customer',
        maxOutputs: 1,
        hasInput: true,
    },
    question: {
        label: 'Ask Question',
        icon: MessageSquare,
        color: 'violet',
        description: 'AI asks a question and waits for response',
        maxOutputs: 1,
        hasInput: true,
    },
    condition: {
        label: 'Condition',
        icon: GitBranch,
        color: 'amber',
        description: 'Branch based on customer response',
        maxOutputs: 10,
        hasInput: true,
    },
    action: {
        label: 'Action',
        icon: Zap,
        color: 'orange',
        description: 'Perform an action (tag, assign, etc.)',
        maxOutputs: 1,
        hasInput: true,
    },
    delay: {
        label: 'Delay',
        icon: Clock,
        color: 'slate',
        description: 'Wait before continuing',
        maxOutputs: 1,
        hasInput: true,
    },
    order: {
        label: 'Create Order',
        icon: ShoppingCart,
        color: 'pink',
        description: 'Create an order for the customer',
        maxOutputs: 2,
        hasInput: true,
    },
    success: {
        label: 'Success',
        icon: CheckCircle,
        color: 'emerald',
        description: 'Conversation ended successfully',
        maxOutputs: 0,
        hasInput: true,
    },
    failure: {
        label: 'End',
        icon: XCircle,
        color: 'red',
        description: 'Conversation ended (no conversion)',
        maxOutputs: 0,
        hasInput: true,
    },
};

const getColorClasses = (color, isSelected) => {
    const colors = {
        emerald: {
            bg: isSelected ? 'bg-emerald-500/30' : 'bg-emerald-500/10',
            border: isSelected ? 'border-emerald-400' : 'border-emerald-500/30',
            icon: 'text-emerald-500',
            handle: 'bg-emerald-500',
        },
        sky: {
            bg: isSelected ? 'bg-sky-500/30' : 'bg-sky-500/10',
            border: isSelected ? 'border-sky-400' : 'border-sky-500/30',
            icon: 'text-sky-500',
            handle: 'bg-sky-500',
        },
        violet: {
            bg: isSelected ? 'bg-violet-500/30' : 'bg-violet-500/10',
            border: isSelected ? 'border-violet-400' : 'border-violet-500/30',
            icon: 'text-violet-500',
            handle: 'bg-violet-500',
        },
        amber: {
            bg: isSelected ? 'bg-amber-500/30' : 'bg-amber-500/10',
            border: isSelected ? 'border-amber-400' : 'border-amber-500/30',
            icon: 'text-amber-500',
            handle: 'bg-amber-500',
        },
        orange: {
            bg: isSelected ? 'bg-orange-500/30' : 'bg-orange-500/10',
            border: isSelected ? 'border-orange-400' : 'border-orange-500/30',
            icon: 'text-orange-500',
            handle: 'bg-orange-500',
        },
        slate: {
            bg: isSelected ? 'bg-slate-500/30' : 'bg-slate-500/10',
            border: isSelected ? 'border-slate-400' : 'border-slate-500/30',
            icon: 'text-slate-400',
            handle: 'bg-slate-500',
        },
        pink: {
            bg: isSelected ? 'bg-pink-500/30' : 'bg-pink-500/10',
            border: isSelected ? 'border-pink-400' : 'border-pink-500/30',
            icon: 'text-pink-500',
            handle: 'bg-pink-500',
        },
        red: {
            bg: isSelected ? 'bg-red-500/30' : 'bg-red-500/10',
            border: isSelected ? 'border-red-400' : 'border-red-500/30',
            icon: 'text-red-500',
            handle: 'bg-red-500',
        },
    };
    return colors[color] || colors.sky;
};

const FlowNode = ({
    node,
    isSelected,
    isDragging,
    onSelect,
    onDelete,
    onDuplicate,
    onStartConnection,
    onConnect,
    isConnecting,
    connectingFrom,
}) => {
    const nodeType = NODE_TYPES[node.type] || NODE_TYPES.message;
    const Icon = nodeType.icon;
    const colors = getColorClasses(nodeType.color, isSelected);

    const handleMouseDown = (e) => {
        if (e.target.closest('.node-handle') || e.target.closest('.node-action')) {
            return;
        }
        onSelect?.(node.id);
    };

    const handleOutputClick = (e, outputIndex = 0) => {
        e.stopPropagation();
        onStartConnection?.(node.id, outputIndex);
    };

    const handleInputClick = (e) => {
        e.stopPropagation();
        if (isConnecting && connectingFrom) {
            onConnect?.(connectingFrom.nodeId, connectingFrom.outputIndex, node.id);
        }
    };

    const getOutputLabels = () => {
        if (node.type === 'condition') {
            return node.data?.branches?.map(b => b.label) || ['Match', 'No Match'];
        }
        if (node.type === 'order') {
            return ['Success', 'Failed'];
        }
        return [''];
    };

    const outputLabels = getOutputLabels();

    return (
        <div
            className={`absolute group select-none ${isDragging ? 'cursor-grabbing z-50' : 'cursor-grab'}`}
            style={{
                left: node.position.x,
                top: node.position.y,
                width: 264,
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Node Card */}
            <div
                className={`relative w-full rounded-xl border-2 shadow-lg transition-all ${colors.bg} ${colors.border} ${
                    isSelected ? 'ring-2 ring-white/20 shadow-xl' : ''
                } ${isDragging ? 'shadow-2xl' : ''}`}
            >
                {/* Drag Handle */}
                <div className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-1 bg-white/10 rounded cursor-grab">
                        <GripVertical size={14} className="text-white/50" />
                    </div>
                </div>

                {/* Input Handle - RED when connecting, shows on left side */}
                {nodeType.hasInput && (
                    <div
                        className={`node-handle absolute -left-4 top-10 w-8 h-8 rounded-full border-3 flex items-center justify-center cursor-pointer transition-all ${
                            isConnecting
                                ? 'bg-[#ff4d4d] border-white scale-125 shadow-lg shadow-red-500/50 animate-pulse'
                                : 'bg-[#0d1424] border-white/30 hover:border-white hover:scale-110'
                        }`}
                        onClick={handleInputClick}
                        title="Drop connection here"
                    >
                        <ChevronRight size={14} className={isConnecting ? 'text-white' : 'text-white/70'} />
                    </div>
                )}

                {/* Header */}
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
                    <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                        <Icon size={16} className={colors.icon} />
                    </div>
                    <span className="text-sm font-semibold text-white flex-1 truncate">
                        {node.data?.title || nodeType.label}
                    </span>
                </div>

                {/* Content Preview */}
                <div className="px-3 py-2.5 min-h-[44px]">
                    {node.type === 'start' && (
                        <p className="text-xs text-white/50">
                            {node.data?.trigger === 'keyword' ? `Keywords: ${node.data?.keywords || '...'}` : 'When conversation starts'}
                        </p>
                    )}
                    {(node.type === 'message' || node.type === 'question') && (
                        <p className="text-xs text-white/70 line-clamp-2">
                            {node.data?.message || 'Click to add message...'}
                        </p>
                    )}
                    {node.type === 'condition' && (
                        <div className="space-y-1">
                            {node.data?.branches?.slice(0, 2).map((branch, i) => (
                                <div key={i} className="flex items-center gap-1 text-xs text-white/60">
                                    <GitBranch size={10} />
                                    <span className="truncate">{branch.label || `Option ${i + 1}`}</span>
                                </div>
                            ))}
                            {(node.data?.branches?.length || 0) > 2 && (
                                <p className="text-xs text-white/40">+{node.data.branches.length - 2} more</p>
                            )}
                        </div>
                    )}
                    {node.type === 'action' && (
                        <div className="flex items-center gap-1 text-xs text-white/60">
                            <Tag size={10} />
                            <span>{node.data?.actionType || 'Select action...'}</span>
                        </div>
                    )}
                    {node.type === 'delay' && (
                        <p className="text-xs text-white/60">
                            Wait {node.data?.duration || '1'} {node.data?.unit || 'minute'}(s)
                        </p>
                    )}
                    {node.type === 'order' && (
                        <p className="text-xs text-white/60">
                            {node.data?.product || 'Configure order...'}
                        </p>
                    )}
                    {(node.type === 'success' || node.type === 'failure') && (
                        <p className="text-xs text-white/50">
                            {node.data?.message || 'End of flow'}
                        </p>
                    )}
                </div>

                {/* Output Handles - COLORED, on right side */}
                {nodeType.maxOutputs > 0 && (
                    <div className="absolute -right-4 top-10 flex flex-col gap-3">
                        {outputLabels.slice(0, nodeType.maxOutputs).map((label, index) => (
                            <div
                                key={index}
                                className="node-handle flex items-center gap-2 cursor-pointer group/handle"
                                onClick={(e) => handleOutputClick(e, index)}
                                title="Click to start connection"
                            >
                                {outputLabels.length > 1 && label && (
                                    <span className="text-[10px] text-white/40 opacity-0 group-hover:opacity-100 group-hover/handle:opacity-100 transition-opacity whitespace-nowrap bg-[#0d1424] px-1 rounded">
                                        {label}
                                    </span>
                                )}
                                <div
                                    className={`w-8 h-8 rounded-full border-3 flex items-center justify-center transition-all ${colors.handle} border-white/50 hover:scale-125 hover:border-white hover:shadow-lg`}
                                    style={{ boxShadow: `0 0 8px ${colors.handle.replace('bg-', 'rgba(').replace('-500', ', 0.5)')}` }}
                                >
                                    <ChevronRight size={14} className="text-white" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Quick Actions */}
                <div className="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicate?.(node.id);
                        }}
                        className="node-action p-1.5 bg-[#0d1424] border border-white/20 rounded-lg hover:bg-white/10 transition"
                        title="Duplicate"
                    >
                        <Copy size={12} className="text-white/70" />
                    </button>
                    {node.type !== 'start' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete?.(node.id);
                            }}
                            className="node-action p-1.5 bg-[#0d1424] border border-white/20 rounded-lg hover:bg-red-500/20 hover:border-red-500/50 transition"
                            title="Delete"
                        >
                            <Trash2 size={12} className="text-white/70 hover:text-red-500" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FlowNode;
